import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { db, kv, ensureFolders, encryptAll } from "@/lib/db";
import { getVaultKey, setVaultKey } from "@/lib/crypto";
import { afterFail, bioCopy, createVault, failMessage, lockoutLeft, openVault, wrapForPin } from "@/lib/lock";
import { loadAssets } from "@/lib/watermark";
import { ymd } from "@/lib/util";

export const DEFAULT_PROFILE = {
  fullName: "", initials: "", signature: "", logo: "", opacity: 0.3,
  stamp: { name: true, initials: true, signature: false, logo: false },
  apply: { notes: true, media: true, filenames: true, pdfs: true },
};

const Ctx = createContext(null);
export const useVault = () => useContext(Ctx);

function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem("sv-theme") ||
    (window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark"));
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);
  const toggle = () => setTheme((t) => { const n = t === "dark" ? "light" : "dark"; localStorage.setItem("sv-theme", n); return n; });
  return [theme, toggle];
}

function useAutoLock(unlocked, minutes, lock) {
  useEffect(() => {
    if (!unlocked || !minutes) return;
    let t;
    const reset = () => { clearTimeout(t); t = setTimeout(lock, minutes * 60000); };
    const evs = ["pointerdown", "keydown", "scroll", "touchstart", "mousemove"];
    evs.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(t); evs.forEach((e) => window.removeEventListener(e, reset)); };
  }, [unlocked, minutes, lock]);
}

// Panic lock: Esc twice quickly, or the app being hidden (skipped while a file picker/download is open)
function usePanicLock(unlocked, lockOnHide, lock) {
  useEffect(() => {
    if (!unlocked) return;
    let lastEsc = 0, picking = false;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (e.timeStamp - lastEsc < 500) { lock(); toast("Vault locked", { description: "Panic lock" }); }
      lastEsc = e.timeStamp;
    };
    let timer;
    const done = () => { picking = false; clearTimeout(timer); };
    const onClick = (e) => {
      if (!e.target.closest?.('input[type="file"], a[download], label:has(input[type="file"])')) return;
      picking = true; clearTimeout(timer); timer = setTimeout(done, 60000);
    };
    const onFocus = () => setTimeout(() => { if (document.visibilityState === "visible") done(); }, 300);
    const onVis = () => {
      if (document.visibilityState === "visible") { done(); return; }
      if (lockOnHide && !picking) lock();
    };
    window.addEventListener("keydown", onKey, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    document.addEventListener("change", done, true);
    document.addEventListener("cancel", done, true);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("change", done, true);
      document.removeEventListener("cancel", done, true);
      window.removeEventListener("keydown", onKey, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [unlocked, lockOnHide, lock]);
}

export function VaultProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [security, setSecurityState] = useState({});
  const [profile, setProfileState] = useState(DEFAULT_PROFILE);
  const [assets, setAssets] = useState({});
  const [folders, setFolders] = useState([]);
  const [notes, setNotes] = useState([]);
  const [folderId, setFolderId] = useState("daily-journal");
  const [noteId, setNoteId] = useState(null);
  const [view, setView] = useState("folder");
  const [cal, setCal] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth(), selected: ymd(n) }; });
  const [dialog, setDialog] = useState(null);
  const [theme, toggleTheme] = useTheme();

  const refresh = useCallback(async () => {
    setFolders((await db.all("folders")).sort((a, b) => (a.order ?? 99) - (b.order ?? 99) || a.createdAt.localeCompare(b.createdAt)));
    setNotes(await db.all("notes"));
  }, []);

  useEffect(() => {
    (async () => {
      setSecurityState((await kv.get("security")) || {});
      navigator.storage?.persist?.();
      setReady(true);
    })();
  }, []);

  useEffect(() => { loadAssets(profile).then(setAssets); }, [profile]);

  const lock = useCallback(() => {
    setVaultKey(null); setUnlocked(false); setDialog(null); setNoteId(null);
    setNotes([]); setFolders([]); setProfileState(DEFAULT_PROFILE);
  }, []);
  useAutoLock(unlocked, security.autoLock, lock);
  usePanicLock(unlocked, security.lockOnHide !== false, lock);

  const setSecurity = async (s) => { await kv.set("security", s); setSecurityState(s); };
  const setProfile = async (p) => { await kv.set("profile", p); setProfileState(p); };

  const openWithKey = async (key) => {
    setVaultKey(key);
    await encryptAll();
    await ensureFolders();
    const p = await kv.get("profile");
    setProfileState(p ? { ...DEFAULT_PROFILE, ...p } : DEFAULT_PROFILE);
    await refresh();
    setUnlocked(true);
  };

  // Returns { error } or { res, sec } after PBKDF2 unwrap, with persistent attempt lockout
  const checkPin = async (pin) => {
    const sec = (await kv.get("security")) || {};
    const wait = lockoutLeft(sec);
    if (wait) return { error: `Too many attempts — try again in ${Math.ceil(wait / 1000)}s` };
    const res = await openVault(pin, sec);
    if (!res) { const s = afterFail(sec); await setSecurity(s); return { error: failMessage(s) }; }
    let s = { ...sec, fails: 0, lockUntil: 0 };
    if (res.legacy) { const { pinHash, ...rest } = s; s = { ...rest, ...res.sec }; }
    await setSecurity(s);
    return { res, sec: s };
  };

  const createPin = async (pin) => {
    const { key, sec } = await createVault(pin);
    await setSecurity({ ...sec, autoLock: 5 });
    await openWithKey(key);
  };

  const unlockWithPin = async (pin) => {
    const { error, res, sec } = await checkPin(pin);
    if (error) return error;
    if (sec.bioId && !(await kv.get("bioKey"))) await kv.set("bioKey", await bioCopy(res.key));
    await openWithKey(res.key);
    return null;
  };

  const unlockWithBio = async () => {
    const key = await kv.get("bioKey");
    if (!key) throw new Error("Unlock with your PIN once to finish biometric setup");
    await openWithKey(key);
  };

  const verifyPin = async (pin) => (await checkPin(pin)).error || null;

  const changePin = async (cur, next) => {
    const err = await verifyPin(cur);
    if (err) return err;
    const s = (await kv.get("security")) || {};
    await setSecurity({ ...s, ...(await wrapForPin(getVaultKey(), next)) });
    return null;
  };

  const setBiometric = async (bioId) => {
    if (bioId) await kv.set("bioKey", await bioCopy(getVaultKey()));
    else await kv.del("bioKey");
    await setSecurity({ ...security, bioId });
  };

  const value = {
    ready, unlocked, lock, security, setSecurity, profile, setProfile, assets,
    createPin, unlockWithPin, unlockWithBio, verifyPin, changePin, setBiometric,
    folders, notes, refresh, folderId, setFolderId, noteId, setNoteId, view, setView, cal, setCal, dialog, setDialog, theme, toggleTheme,
    folderName: (id) => folders.find((f) => f.id === id)?.name || "Folder",
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
